import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from '@reach/router';
import { useMediaQuery, useTheme } from '@material-ui/core';

import { routes } from '@config/routes';
import { useNavigate } from '@lib/routing';
import { createLocationSearchFromState } from '@modules/shared/helpers';
import { PageStyled } from '@modules/layout/styled';
import { TabsEdit, TabEdit, Loader } from '@modules/layout/moleculas';
import { useBaseLayout } from '@modules/layout/templates';
import { useCurrentUser } from '@modules/user/hooks';
import { useStudent } from '@modules/students/hooks';
import { StudentEditForm, StudentEditFormTypesEnum } from '@modules/students/organisms';
import { UserGrantCategoryToken, UserGrantActionToken } from '@modules/types/graphql';

import type { Page } from '@modules/types/page';

type StudentsEditEntryProps = Page & {
    backRouteUrl: string;
    backRouteHash?: string;
};

const studentEditFormTypeByIndex = [
    StudentEditFormTypesEnum.summary,
    StudentEditFormTypesEnum.education,
    StudentEditFormTypesEnum.access,
];

const StudentsEditEntry = (props: StudentsEditEntryProps): React.ReactElement => {
    const { title = '', backRouteUrl, backRouteHash = '' } = props;

    const { t } = useTranslation(['common', 'access', 'students']);

    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();
    const studentId: string = params.id;

    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });

    const { currentUser } = useCurrentUser();
    const { studentGrants } = currentUser.can({
        [UserGrantCategoryToken.student]: [
            UserGrantActionToken.update,
            UserGrantActionToken.delete,
            UserGrantActionToken.viewAccess,
            UserGrantActionToken.createAccess,
            UserGrantActionToken.sendAccess,
            UserGrantActionToken.deleteAccess,
        ],
    });

    const { setPageTitle, setRedirect } = useBaseLayout();

    const {
        student,
        data: studentData,
        loading: studentLoading,
    } = useStudent({
        fetchPolicy: 'network-only',
        skip: !studentId,
        variables: { id: studentId },
    });

    const [currentTab, setCurrentTab] = React.useState<number>(0);

    const handleChangeTab = (_: any, value: any): void => setCurrentTab(value);

    const handleOnDeleteStudent = (): void => {
        const backLinkState = { ...(location.state as Record<string, any>) };
        const backLink = createLocationSearchFromState({ state: backLinkState });

        navigate(backLink, { state: backLinkState });
    };

    React.useEffect(() => {
        if (studentId) {
            const handleRedirect = (): void => {
                const backLinkState = { ...(location.state as Record<string, any>) };
                const backPath = backLinkState?.backPath;

                if (backPath) delete backLinkState.backPath;

                const backLink =
                    createLocationSearchFromState({
                        pathname: backPath ?? backRouteUrl ?? routes.students.path,
                        state: backLinkState,
                    }) + backRouteHash;

                navigate(backLink, { state: backLinkState });
            };

            setRedirect(() => handleRedirect);
        }
    }, [studentId, backRouteUrl]);

    React.useEffect(() => {
        if (studentData) {
            setPageTitle(
                `${title ? t(title) : ''} ${student.fullName ? `"${student.fullName}"` : ''}`,
            );
        }
    }, [t, studentData]);

    if (studentLoading) {
        return (
            <PageStyled.PageEdit>
                <Loader />
            </PageStyled.PageEdit>
        );
    }

    return (
        <PageStyled.PageEdit>
            <TabsEdit
                orientation={isDesktop ? 'vertical' : 'horizontal'}
                value={currentTab}
                onChange={handleChangeTab}
            >
                <TabEdit label={t('students:tabs.summary')} />
                <TabEdit label={t('students:tabs.education')} />

                {studentGrants.canViewAccess ? <TabEdit label={t('access:titles.access')} /> : null}
            </TabsEdit>

            <PageStyled.PageEditContainer>
                <StudentEditForm
                    canEdit={studentGrants.canUpdate}
                    canViewAccess={studentGrants.canViewAccess}
                    canCreateAccess={studentGrants.canCreateAccess}
                    canSendAccess={studentGrants.canSendAccess}
                    canDeleteAccess={studentGrants.canDeleteAccess}
                    canDeleteStudent={
                        studentGrants.canDelete && currentUser.school.canDeleteStudent
                    }
                    type={studentEditFormTypeByIndex[currentTab]}
                    student={student}
                    onDeleteStudent={handleOnDeleteStudent}
                />
            </PageStyled.PageEditContainer>
        </PageStyled.PageEdit>
    );
};

export { StudentsEditEntry };
